/* Index workspace — the directory of things the caller's library recommends.

   Structure follows §8 (browsing workspaces share one mental model): title →
   sticky kind chips → content. Decisions worth knowing:

   - One payload, filtered locally. /api/mentions returns the whole aggregate
     (~1.3k rows on the owner's library) and takes no parameters, so search and
     kind filtering are both client-side. Search matches name AND description —
     "scraping" should find Apify.
   - "All" groups into per-kind sections with sticky headers, because a flat
     1,320-row list is a wall. Picking a kind drops the headers entirely: one
     section titled the same as the chip you just pressed is pure duplication
     (§23).
   - Chip counts follow the search, so they answer "how many matches per kind",
     not "how big is the library".
   - A row opens the mentioning items as a playlist in Detail, matching v1.
   - Hide is the row's one action (ADR 0051). It is permanent — there is no
     unhide endpoint — so it states what it does before the tap rather than
     after it. On touch that is the action sheet; on a fine pointer the hover
     button still asks first. (When an unhide endpoint exists, this becomes an
     Undo toast.) */

import { LitElement, html, css } from 'lit';
import { customElement, property, state, query as domQuery } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import { isCoarsePointer } from '../../core/platform.js';
import type { Intent, Mention, WorkspaceDef } from '../../core/types.js';
import { getMentions, hideMention, isSignedIn } from '../../core/api.js';
import { prettyKind } from '../../../../packages/core/index.js';
import { watchBandCollapse, type ScrollDirectionWatcher } from '../../core/scroll-direction.js';
import type { ChipOption } from '../../component/g-chips/g-chips.js';
import type { GDialog } from '../../component/g-dialog/g-dialog.js';
import type { SheetAction } from '../../component/g-action-sheet/g-action-sheet.js';
import type { GFilterBar } from '../../component/g-filter-bar/g-filter-bar.js';
import '../../component/g-filter-bar/g-filter-bar.js';
import './index-row.js';
import '../../component/g-action-sheet/g-action-sheet.js';
import '../../component/g-dialog/g-dialog.js';
import '../../component/g-button/g-button.js';
import '../../component/g-notice/g-notice.js';

export const indexDef: WorkspaceDef = {
  id: 'index',
  label: 'Index',
  icon: 'index',
  tag: 'g-index',
  zone: 'top',
  searchable: true,
  searchPlaceholder: 'Search names and descriptions…',
  refreshable: true,
};

/** The one thing a row can do (ADR 0051). Native's entry sheet says the same. */
const HIDE_NOTE = 'Hides it from your Index.';

/** What the action sheet offers. */
const ACTIONS: SheetAction[] = [{ id: 'hide', label: 'Hide', icon: 'eye-off', note: HIDE_NOTE }];

@customElement('g-index')
export class GIndex extends LitElement {
  static override styles = [
    shadowReset,
    css`
      :host {
        display: block;
        /* Set by the ResizeObserver below; the fallback covers the first paint. */
        --bar-h: 62px;
      }
      /* No page title: the rail already names the active workspace, so an <h1>
         repeating it is a second label for the same thing (§23). The chips are
         the first thing in the workspace.

         The band itself is g-filter-bar (ADR 0017); --bar-h is measured from it,
         never hardcoded, so the sticky .kind offset below follows it. */
      /* --gutter-outer, not a fixed inset: on mobile it is 0 so the section (and
         the rows inside it) go flush to the screen edge and each row insets its own
         content instead. On desktop it is 24px, the document idiom. */
      .sections {
        padding: 0 var(--gutter-outer) var(--space-6);
      }
      /* Sits directly beneath the chip bar when stuck, hence the --bar-h offset
         rather than 0.

         Deliberately the strongest mark in the content area. At 0.72rem dim it was
         the FAINTEST thing on a page of bold entry names — the clearest grouping
         signal rendered as the quietest, which is an inverted hierarchy. It now
         outranks a row name (0.9rem/600) on size, weight and colour. */
      .kind {
        position: sticky;
        top: var(--bar-h);
        /* Rides with the band: --bar-h goes to 0 and this interpolates, because a
           changed custom property changes the computed value of a property that
           can animate.

           It has to be top and not a transform — a transform would displace
           every UNSTUCK header too, since transforms apply after layout and there
           is no way to scope one to the stuck state. Matched to the band's own
           direction-dependent timing so the two move as one thing. */
        transition: top 150ms cubic-bezier(0, 0, 0.2, 1);
        z-index: 2;
        display: flex;
        align-items: baseline;
        gap: var(--space-2);
        margin: 0;
        /* Left padding is the same --row-pad-l index-row uses, so the kind label
           and the entry names share a left edge at both widths. The top padding is part of the sticky box, so it also
           sets the band of background shown above the label while stuck — space-5
           left too much dead space there and above the first section. */
        padding: var(--space-3) 0 var(--space-2) var(--row-pad-l);
        background: var(--bg);
        /* Brand accent, matching the emphasis marks in Settings. Affordable here
           precisely because there are only 14 of them and they sit at the top of
           the hierarchy — the same colour on the per-row ×N badges was noise, at a
           hundred instances annotating the names they competed with. */
        color: var(--accent);
        font-family: var(--font-display);
        font-size: 1.05rem;
        font-weight: 700;
      }
      :host([data-collapsed]) .kind {
        transition: top 200ms cubic-bezier(0.4, 0, 1, 1);
      }
      .kind .rule {
        flex: 1;
        height: 1px;
        margin-left: var(--space-2);
        background: var(--border);
      }
      /* Beside the label, never right-aligned. At this width a right-aligned count
         sits ~1,500px from the word it counts. */
      .kind .n {
        color: var(--text-dim);
        font-family: var(--font-mono);
        font-size: 0.78rem;
        font-weight: 400;
      }
      /* Plain block flow: one entry per line, one reading axis. The alignment that
         groups these lives inside index-row, not here. */
      .rows {
        display: block;
      }
      .flat {
        padding-top: var(--space-4);
      }
      /* Only the inset is local — g-notice owns typography, colour and role. A
         notice is content in its own right, so it uses --content-* (text needs its
         inset wherever it lives), not the full-bleed row treatment. */
      g-notice.page {
        padding: var(--space-6) var(--content-r) var(--space-6) var(--content-l);
      }
      /* §21 — skeletons over spinners. Row-shaped, so the loading state has the
         same silhouette as the content that replaces it. */
      .skel {
        height: 20px;
        margin: var(--space-2) var(--row-pad-r)
          var(--space-2) var(--row-pad-l);
        border-radius: var(--radius-sm);
        background: var(--surface);
        animation: pulse 1.4s ease-in-out infinite;
      }
      @keyframes pulse {
        50% {
          opacity: 0.45;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .skel {
          animation: none;
        }
        .kind {
          transition: none;
        }
      }
      .warn {
        margin: 0 0 var(--space-2);
        font-size: 0.9rem;
        line-height: 1.45;
      }
    `,
  ];

  @property() query = '';

  @state() private rows: Mention[] = [];
  @state() private kind = '';
  @state() private loading = true;
  @state() private error = '';
  /** The row whose action sheet is up; touch only, since only that arm has a ⋮. */
  @state() private menuFor: Mention | null = null;
  /** The fine-pointer confirm, whose icon carries no consequence text. */
  @state() private pending: Mention | null = null;
  /** Read once, per core/platform: a phone does not become a desktop. */
  private readonly touch = isCoarsePointer();
  /**
   * Band out of the way while reading. See ADR 0017 §4.
   *
   * Deliberately NOT @state: this flips mid-fling, and a reactive write would
   * re-run this workspace's render — walking 1,320 rows for Lit to diff and
   * discard — at the one moment there is no frame budget for it. Everything that
   * actually has to change (the band's attribute, --bar-h, the host flag driving
   * the header's own timing) is a non-reactive write, so `applyCollapse` does all
   * three directly.
   */
  private collapsed = false;

  @domQuery('g-dialog') private dialog?: GDialog;
  @domQuery('g-filter-bar') private bar?: GFilterBar;
  private observer?: ResizeObserver;
  private bandWatch?: ScrollDirectionWatcher;
  /** Last measured band height, so a collapse needs no layout read. */
  private barH = 0;

  override connectedCallback(): void {
    super.connectedCallback();
    void this.load();
    // The host IS the scrollport — `#outlet > *` is where each workspace's own
    // scroll lives (ADR 0010) — so it is both the element to watch and the one
    // whose `hidden` says we came back to the front.
    this.bandWatch = watchBandCollapse(this, this, (c) => this.applyCollapse(c), {
      onReturn: () => this.bar?.closeSearch(),
    });
  }

  /** Straight to the DOM, off Lit's render path — see the field's own note. */
  private applyCollapse(collapsed: boolean): void {
    this.collapsed = collapsed;
    if (this.bar) this.bar.collapsed = collapsed;
    // Mirrored on the host so the kind headers can match the band's own timing,
    // which is asymmetric by direction.
    this.toggleAttribute('data-collapsed', collapsed);
    this.applyBarOffset();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.observer?.disconnect();
    this.bandWatch?.stop();
  }

  override firstUpdated(): void {
    const bar = this.bar;
    if (!bar) return;
    // Window resizes rewrap the chip rows without triggering a Lit update, so the
    // observer covers what updated() can't see.
    this.observer = new ResizeObserver(() => this.measureBar());
    this.observer.observe(bar);
  }

  /**
   * Back to the top, because the content set has been replaced.
   *
   * §11 preserves state across *navigation*; a new query is a new set, and keeping
   * the offset drops you into the middle of a list you have not seen the start of.
   * A direct write: the host IS the scrollport (ADR 0010).
   */
  private toTop(): void {
    this.scrollTop = 0;
  }

  override willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('query')) this.toTop();
  }

  override updated(): void {
    // Two reasons this isn't left to the observer. ResizeObserver delivery is tied
    // to the rendering steps, so the headers would sit at the fallback offset
    // until the first frame; and a parent's updated() runs BEFORE its children
    // render, so measuring now reads a bar that has no chips in it yet — hence
    // awaiting the chips first.
    this.measureBar();
    void this.bar?.updateComplete.then(() => this.measureBar());
  }

  /** offsetHeight, not contentRect — headers must clear the bar's padding and
      border too, which contentRect excludes. The result is cached, because reading
      it forces a synchronous layout of the whole list and the collapse path must
      never pay that: measured on the device, doing so cost 98ms frames. */
  private measureBar(): void {
    const h = this.bar?.offsetHeight;
    if (h) this.barH = h;
    this.applyBarOffset();
  }

  /**
   * Point the sticky kind headers at the band's current edge.
   *
   * Writes only, no reads, so this is safe on the scroll path. Zero while the band
   * is collapsed: offsetHeight ignores the transform that took it off screen, so
   * without this the headers would stick a band's height below a band that is no
   * longer there.
   */
  private applyBarOffset(): void {
    this.style.setProperty('--bar-h', this.collapsed ? '0px' : `${this.barH}px`);
  }

  /**
   * Refetch the mention aggregate. Called by the shell's pull-to-refresh — see
   * `WorkspaceDef.refreshable` and ADR 0010.
   *
   * Deliberately does not clear `rows` first: /api/mentions returns the whole
   * aggregate in one response, so swapping it in on arrival redraws once, where
   * blanking would flash the list empty for the length of the request.
   */
  async refresh(): Promise<void> {
    this.error = '';
    await this.load();
  }

  private async load(): Promise<void> {
    try {
      const rows = await getMentions();
      if (isSignedIn(rows)) this.rows = rows;
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    }
    this.loading = false;
  }

  /** Rows matching the header query — name or description, case-insensitive. */
  private get searched(): Mention[] {
    const q = this.query.trim().toLowerCase();
    if (!q) return this.rows;
    return this.rows.filter(
      (m) => m.name.toLowerCase().includes(q) || m.detail.toLowerCase().includes(q),
    );
  }

  /** "All" then every kind present, largest first — the chip order. */
  private get counts(): ChipOption[] {
    const by = new Map<string, number>();
    for (const m of this.searched) by.set(m.kind, (by.get(m.kind) ?? 0) + 1);
    const kinds = [...by.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([kind, n]) => ({ value: kind, label: prettyKind(kind), n }));
    return [{ value: '', label: 'All', n: this.searched.length }, ...kinds];
  }

  /**
   * The selected kind, or All when the selection has no matches left.
   *
   * Derived rather than corrected in state, so narrowing the search past the
   * selected kind falls back to All and then *restores* the selection when the
   * search is cleared. v1 discards it permanently.
   */
  private get activeKind(): string {
    return this.counts.some((c) => c.value === this.kind) ? this.kind : '';
  }

  private get visible(): Mention[] {
    const kind = this.activeKind;
    return kind ? this.searched.filter((m) => m.kind === kind) : this.searched;
  }

  /** [kind, rows] with the biggest section first, matching the chip order. */
  private get grouped(): [string, Mention[]][] {
    const by = new Map<string, Mention[]>();
    for (const m of this.visible) {
      const bucket = by.get(m.kind);
      if (bucket) bucket.push(m);
      else by.set(m.kind, [m]);
    }
    return [...by.entries()].sort((a, b) => b[1].length - a[1].length);
  }

  /** Open Detail on everything that mentions this entry, as one playlist. */
  private openEntry(entry: Mention): void {
    if (!entry.reel_ids.length) return;
    this.dispatchEvent(
      new CustomEvent('open-detail', {
        detail: {
          ids: [...entry.reel_ids],
          at: 0,
          source: { workspace: 'index', label: entry.name },
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * `open-search` is the tab bar's entry (ADR 0019) — the band is always here, so
   * there is nothing to prepare.
   *
   * Otherwise: Detail removed an item. These rows are aggregates over it, so the
   * count drops and a row whose last item is gone goes with it — the alternative
   * is an Index entry that opens nothing.
   */
  applyIntent(intent: Intent): void {
    if (intent.kind === 'open-search') return void this.bar?.openSearch();
    if (intent.kind !== 'item-deleted') return;
    this.rows = this.rows
      .map((m) =>
        m.reel_ids.includes(intent.id)
          ? { ...m, reel_ids: m.reel_ids.filter((x) => x !== intent.id), n: Math.max(0, m.n - 1) }
          : m,
      )
      .filter((m) => m.reel_ids.length > 0);
  }

  /** Fine pointer only: the icon says nothing about what it does, so ask first. */
  private ask(entry: Mention): void {
    this.pending = entry;
    void this.updateComplete.then(() => this.dialog?.show());
  }

  /**
   * Close and clear.
   *
   * Cancel and confirm both call this rather than leaning on the dialog's
   * `g-close` event: that event stays wired for the paths only the platform knows
   * about (Escape, backdrop click), but making the button paths explicit means a
   * missed event can't strand a closed dialog in the DOM.
   */
  private dismiss(): void {
    this.dialog?.close();
    this.pending = null;
  }

  private async confirm(): Promise<void> {
    const p = this.pending;
    if (!p) return;
    await this.run(p);
    this.dismiss();
  }

  /**
   * Hide an entry and drop the row.
   *
   * Called once the user has been told what it does: by the sheet, which says so
   * on the button itself, or by the dialog the hover button raises.
   */
  private async run(entry: Mention): Promise<void> {
    this.error = '';
    try {
      const r = await hideMention(entry.name, entry.kind);
      if (!isSignedIn(r)) return;
      // Hiding is permanent, so drop it locally instead of refetching 1.3k rows.
      this.rows = this.rows.filter((m) => !(m.name === entry.name && m.kind === entry.kind));
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    }
  }

  private renderRows(rows: Mention[]) {
    return html`
      <div class="rows">
        ${rows.map(
          (m) => html`
            <index-row
              .entry=${m}
              .touch=${this.touch}
              @open=${() => this.openEntry(m)}
              @menu=${() => (this.menuFor = m)}
              @hide=${() => this.ask(m)}
            ></index-row>
          `,
        )}
      </div>
    `;
  }

  private renderBody() {
    if (this.loading) {
      return html`<div class="sections flat">
        ${Array.from({ length: 12 }, () => html`<div class="skel"></div>`)}
      </div>`;
    }
    if (!this.visible.length) {
      return html`<g-notice class="page">
        ${this.query.trim()
          ? html`Nothing matches “${this.query.trim()}”.`
          : 'Nothing here yet — saved items fill this in as they are analysed.'}
      </g-notice>`;
    }
    // A kind is selected: one section, and its header would just repeat the chip.
    if (this.activeKind) {
      return html`<div class="sections flat">${this.renderRows(this.visible)}</div>`;
    }
    return html`
      <div class="sections">
        ${this.grouped.map(
          ([kind, rows]) => html`
            <h2 class="kind">
              <span>${prettyKind(kind)}</span>
              <span class="n">${rows.length.toLocaleString()}</span>
              <span class="rule"></span>
            </h2>
            ${this.renderRows(rows)}
          `,
        )}
      </div>
    `;
  }

  private renderMenu() {
    const m = this.menuFor;
    if (!m) return '';
    return html`
      <g-action-sheet
        heading=${m.name}
        meta=${`${prettyKind(m.kind)} · ${m.n} item${m.n === 1 ? '' : 's'}`}
        .actions=${ACTIONS}
        @act=${() => void this.run(m)}
        @dismiss=${() => (this.menuFor = null)}
      ></g-action-sheet>
    `;
  }

  private renderDialog() {
    const p = this.pending;
    if (!p) return '';
    return html`
      <g-dialog heading=${`Hide “${p.name}”?`} @g-close=${() => (this.pending = null)}>
        <p class="warn">${HIDE_NOTE}</p>
        <g-button slot="actions" @click=${this.dismiss}>Cancel</g-button>
        <g-button slot="actions" variant="primary" @click=${this.confirm}>Hide</g-button>
      </g-dialog>
    `;
  }

  override render() {
    return html`
      <!-- No .collapsed binding: applyCollapse owns that property, so a render
           triggered by anything else cannot fight it. -->
      <g-filter-bar
        .options=${this.counts}
        .selected=${this.activeKind}
        .searchable=${true}
        .placeholder=${indexDef.searchPlaceholder ?? ''}
        .query=${this.query}
        @select=${(e: CustomEvent<string>) => {
          this.kind = e.detail;
          this.toTop();
        }}
        @search-close=${() => this.bandWatch?.reset()}
      ></g-filter-bar>

      ${this.error
        ? html`<g-notice class="page" tone="danger">${this.error}</g-notice>`
        : ''}
      ${this.renderBody()}
      ${this.renderMenu()} ${this.renderDialog()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-index': GIndex;
  }
}
